import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import { cn } from '@/common/utils/cn'
import CreateForwardingSelectDirectiveEntrySelectLoading from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/components/CreateForwardingSelectDirectiveEntrySelectLoading'
import CreateMaintenanceForwardingHeader from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/components/CreateMaintenanceForwardingHeader'
import { useMaintenanceForwardingsValidDirectives } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/hooks/useMaintenanceFowardingsValidDirectives'
import {
  selectDirectiveEntrySchema,
  SelectDirectiveEntryValidationSchema,
} from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/schemas/maintenanceForwarding.schema'
import { MaintenanceForwardingsValidDirectiveResponse } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceFowardingsValidDirective.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { SquareXIcon } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

const CreateMaintenanceForwardingSelectDirectiveEntry = () => {
  const { id: workflowId } = useParams()

  const navigate = useNavigate()

  const {
    data: forwardingsValidDirectives,
    isPending: forwardingsValidDirectivesIsPending,
    error: forwardingsValidDirectivesError = {} as any,
    refetch: refetchForwardingsValidDirectives,
  } = useMaintenanceForwardingsValidDirectives<MaintenanceForwardingsValidDirectiveResponse>({
    filters: {
      workflowId,
    },
  })

  const { setValue, control, handleSubmit } = useForm<SelectDirectiveEntryValidationSchema>({
    resolver: zodResolver(selectDirectiveEntrySchema),
  })

  const { directive_entry_type: watchDirectiveEntryType } = useWatch({
    control,
  })

  return (
    <div className="inner-screen">
      <CreateMaintenanceForwardingHeader
        showCancelButton
        actions={
          <Button disabled={!watchDirectiveEntryType} onClick={handleSubmit(() => navigate(watchDirectiveEntryType!))}>
            Confirmar
          </Button>
        }
      />

      <Box className="flex-1 overflow-auto p-4">
        <ErrorMessageWrapper
          statusCode={forwardingsValidDirectivesError?.status}
          refetchFunction={refetchForwardingsValidDirectives}
          className="h-[calc(100vh-128px-32px)]"
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <p className="font-bold">Encaminhamento de processo</p>

              <p className="text-sm text-white-400">
                Decida a forma como deseja prosseguir com esse processo de manutenção
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {forwardingsValidDirectivesIsPending && (
                <>
                  <CreateForwardingSelectDirectiveEntrySelectLoading />

                  <CreateForwardingSelectDirectiveEntrySelectLoading />
                </>
              )}

              {!forwardingsValidDirectivesIsPending &&
                forwardingsValidDirectives &&
                Object.entries(forwardingsValidDirectives).map(([directiveKlass, directive]) => (
                  <button
                    key={directiveKlass}
                    className={cn(
                      'bg-white-100 hover:bg-white-200 transition-colors cursor-pointer rounded-md p-4 flex flex-col items-start gap-2 disabled:opacity-70 disabled:bg-white-100/5 disabled:text-white-400 disabled:cursor-default disabled:pointer-events-none',
                      watchDirectiveEntryType === directiveKlass && 'bg-success-op hover:bg-success-op/25',
                    )}
                    onClick={() => setValue('directive_entry_type', directiveKlass)}
                    disabled={!directive.can_create}
                  >
                    <div className="flex items-center justify-between w-full gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <div className="min-w-3 min-h-3 max-w-3 max-h-3 rounded-full border border-white-400 flex items-center justify-center">
                          {watchDirectiveEntryType === directiveKlass && (
                            <div className="bg-white-800 size-1.5 rounded-full" />
                          )}
                        </div>

                        <p className="font-bold text-sm text-left">{directive.name}</p>
                      </div>

                      {directive.can_create === false && (
                        <div className="flex items-center gap-1 text-white-300 ml-auto">
                          <SquareXIcon size={16} />

                          <span className="text-xs italic">Desabilitado</span>
                        </div>
                      )}
                    </div>

                    {directive.description && (
                      <div>
                        <p className="text-xs text-white-400 whitespace-pre-wrap text-left">{directive.description}</p>
                      </div>
                    )}
                  </button>
                ))}
            </div>
          </div>
        </ErrorMessageWrapper>
      </Box>
    </div>
  )
}

export default CreateMaintenanceForwardingSelectDirectiveEntry
